import { useEffect, useRef, useState } from "react";
import { FaMagnifyingGlassLocation } from "react-icons/fa6";
import {
  FaBolt,
  FaCheck,
  FaEdit,
  FaInfoCircle,
  FaTimes,
  FaWrench,
} from "react-icons/fa";
import { MdDeviceHub } from "react-icons/md";
import { TbMapPinBolt } from "react-icons/tb";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { useConfig } from "../../context/configContext";
import "../../App.css";
import api from "../interceptors";
import "./index.css";

const formatLabel = (label = "") => label.replaceAll("_", " ");

const Field = ({ label, value, name, onChange, editable }) => (
  <div className="cp-field">
    <label>{formatLabel(label)}</label>
    <input
      type="text"
      name={name}
      value={value || ""}
      onChange={onChange}
      readOnly={!editable}
      className={`cp-input ${editable ? "is-editable" : "is-readonly"}`}
    />
  </div>
);

const ChargerProfile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [charger, setCharger] = useState({});
  const { config } = useConfig();
  const [cpoFound, setCpoFound] = useState(false);
  const [cpoResults, setCpoResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchTimerRef = useRef(null);

  const normalizeStationData = (rows) => {
    if (!rows || rows.length === 0) return null;

    const first = rows[0];

    const result = {
      station_id: first.station_id,
      charger_name: first.charger_name,
      customer_name: first.customer_name,
      customer_id: first.customer_id,
      location: {
        address: first.address,
        latitude: first.latitude,
        longitude: first.longitude,
        state: first.state,
        district: first.state_district,
        pincode: first.pincode,
        country: first.country,
      },
      technician: {
        name: first.responsible_technician,
        contact_no: first.contact_no,
      },
      dispensers: {},
    };

    rows.forEach((row) => {
      const dId = row.dispensor_id;

      if (!result.dispensers[dId]) {
        result.dispensers[dId] = {
          dispensor_id: dId,
          dispensor_name: row.dispensor_name,
          guns: [],
        };
      }

      result.dispensers[dId].guns.push({
        gun_id: row.gun_id,
        power: row.power,
      });
    });

    result.dispensers = Object.values(result.dispensers);

    return result;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCharger({ ...charger, [name]: value });

    if (name === "customer_id") {
      clearTimeout(searchTimerRef.current);

      setCharger((prev) => ({
        ...prev,
        customer_id: value,
        customer_name: "",
      }));

      searchTimerRef.current = setTimeout(async () => {
        if (value.length < 2) {
          setCpoResults([]);
          setShowDropdown(false);
          setCharger((prev) => ({
            ...prev,
            customer_name: "",
          }));
          return;
        }

        try {
          const res = await api.get(`/api/cpo/search/${value}`);
          const results = res.data?.results || [];

          setCpoResults(results);
          setShowDropdown(true);
          setCpoFound(results.length > 0);

          if (results.length === 0) {
            setCharger((prev) => ({
              ...prev,
              customer_name: "",
            }));
          }
        } catch (err) {
          console.error("CPO search failed", err);
          setCpoResults([]);
          setShowDropdown(false);
          setCpoFound(false);
        }
      }, 300);
    }
  };

  useEffect(() => {
    if (!config?.station_id) return undefined;

    const fetchData = async () => {
      try {
        const res = await api.get(`/api/station/${config.station_id}`);
        const data = res.data;

        if (data.success && data.data.length > 0) {
          setCharger(normalizeStationData(data.data));
        }
      } catch (err) {
        console.error("Error fetching charger:", err);
      }
    };

    fetchData();

    return () => clearTimeout(searchTimerRef.current);
  }, [config.station_id]);

  const handleSubmit = async () => {
    if (!charger.customer_name) {
      toast.error("Enter CPO Name before submitting");
      return;
    }

    try {
      const res = await api.put(`/api/station/${charger.station_id}`, charger);
      const data = res.data;

      if (data.success) {
        toast.success("Updated successfully");
        setIsEditing(false);
      } else {
        toast.error("Update failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong");
    }
  };

  return (
    <main className="charger-profile-shell">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        style={{ zIndex: 99999 }}
        theme="colored"
      />

      <header className="cp-hero">
        <div>
          <p className="cp-eyebrow">
            <FaBolt />
            Charger Profile
          </p>
          <h1>{charger.charger_name || "Station profile"}</h1>
          <div className="cp-meta-row">
            {charger.station_id && <span>Station {charger.station_id}</span>}
            {charger.customer_name && <span>{charger.customer_name}</span>}
            {charger.location?.address && <span>{charger.location.address}</span>}
          </div>
        </div>

        <div className="cp-actions">
          {isEditing && (
            <button
              onClick={handleSubmit}
              type="button"
              className="cp-button cp-button-primary"
            >
              <FaCheck />
              Submit
            </button>
          )}

          <button
            onClick={() => setIsEditing((value) => !value)}
            type="button"
            className={`cp-button cp-button-secondary ${
              isEditing ? "is-editing" : ""
            }`}
          >
            {isEditing ? <FaTimes /> : <FaEdit />}
            {isEditing ? "Cancel" : "Edit"}
          </button>
        </div>
      </header>

      {!charger?.station_id ? (
        <div className="cp-loading">Loading charger profile...</div>
      ) : (
        <div className="cp-layout">
          <section className="cp-panel hide-scrollbar">
            <section className="cp-section">
              <div className="cp-section-heading">
                <h2 className="cp-section-title">
                  <FaInfoCircle />
                  General Info
                </h2>
                <span className="cp-status-chip">
                  {cpoFound ? "Existing CPO" : "Station details"}
                </span>
              </div>

              <div className="cp-grid">
                <Field
                  label="Station ID"
                  value={charger.station_id}
                  editable={false}
                />
                <Field
                  label="Charger Name"
                  name="charger_name"
                  value={charger.charger_name}
                  onChange={handleChange}
                  editable={isEditing}
                />

                <div className="relative">
                  <Field
                    label="Customer ID"
                    name="customer_id"
                    value={charger.customer_id}
                    onChange={handleChange}
                    editable={isEditing}
                  />

                  {showDropdown && cpoResults.length > 0 && (
                    <div className="cp-search-menu">
                      {cpoResults.map((cpo) => (
                        <button
                          type="button"
                          key={cpo.customer_id}
                          className="cp-search-option"
                          onClick={() => {
                            setCharger((prev) => ({
                              ...prev,
                              customer_id: cpo.customer_id,
                              customer_name: cpo.name,
                            }));
                            setCpoFound(true);
                            setShowDropdown(false);
                          }}
                        >
                          {cpo.customer_id}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <Field
                  label="Customer"
                  value={charger.customer_name}
                  name="customer_name"
                  onChange={handleChange}
                  editable={isEditing && !cpoFound}
                />
              </div>
            </section>

            <section className="cp-section">
              <h2 className="cp-section-title">
                <FaMagnifyingGlassLocation />
                Location
              </h2>

              <div className="cp-grid">
                {Object.entries(charger.location || {}).map(([key, value]) => (
                  <Field
                    key={key}
                    label={key}
                    value={value}
                    name={key}
                    onChange={(e) =>
                      setCharger({
                        ...charger,
                        location: {
                          ...charger.location,
                          [key]: e.target.value,
                        },
                      })
                    }
                    editable={isEditing}
                  />
                ))}
              </div>
            </section>

            <section className="cp-section">
              <h2 className="cp-section-title">
                <FaWrench />
                Technician
              </h2>

              <div className="cp-grid">
                <Field
                  label="Name"
                  value={charger.technician?.name}
                  editable={isEditing}
                  onChange={(e) =>
                    setCharger({
                      ...charger,
                      technician: {
                        ...charger.technician,
                        name: e.target.value,
                      },
                    })
                  }
                />

                <Field
                  label="Contact"
                  value={charger.technician?.contact_no}
                  editable={isEditing}
                  onChange={(e) =>
                    setCharger({
                      ...charger,
                      technician: {
                        ...charger.technician,
                        contact_no: e.target.value,
                      },
                    })
                  }
                />
              </div>
            </section>

            <section className="cp-section">
              <h2 className="cp-section-title">
                <MdDeviceHub />
                Dispensers
              </h2>

              <div className="cp-dispenser-list">
                {charger.dispensers?.map((dispenser) => (
                  <article
                    key={dispenser.dispensor_id}
                    className="cp-dispenser-card"
                  >
                    <h4>
                      {dispenser.dispensor_name} ({dispenser.dispensor_id})
                    </h4>

                    <div className="cp-gun-grid">
                      {dispenser.guns.map((gun) => (
                        <div key={gun.gun_id} className="cp-gun-field">
                          <label>{gun.gun_id}</label>
                          <input
                            type="text"
                            value={gun.power}
                            readOnly={!isEditing}
                            onChange={(e) => {
                              const updated = charger.dispensers.map((item) =>
                                item.dispensor_id === dispenser.dispensor_id
                                  ? {
                                      ...item,
                                      guns: item.guns.map((entry) =>
                                        entry.gun_id === gun.gun_id
                                          ? { ...entry, power: e.target.value }
                                          : entry,
                                      ),
                                    }
                                  : item,
                              );

                              setCharger({ ...charger, dispensers: updated });
                            }}
                            className={`cp-input ${
                              isEditing ? "is-editable" : "is-readonly"
                            }`}
                          />
                        </div>
                      ))}
                    </div>
                  </article>
                ))}
              </div>
            </section>
          </section>

          <aside className="cp-map-panel">
            <div className="cp-map-heading">
              <h2 className="cp-map-title">
                <TbMapPinBolt />
                Map
              </h2>
              <span className="cp-status-chip">
                {charger.location?.latitude && charger.location?.longitude
                  ? "Pinned"
                  : "No pin"}
              </span>
            </div>

            <div className="cp-map-frame">
              <iframe
                title="map"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
                src={`https://www.google.com/maps?q=${charger.location?.latitude},${charger.location?.longitude}&z=10&output=embed`}
              ></iframe>
            </div>
          </aside>
        </div>
      )}
    </main>
  );
};

export default ChargerProfile;
